import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../Styles/O2CAgent.css';
import {
  ingestPurchaseOrder,
  confirmIngestedPurchaseOrder,
  getOrders,
  getOrderDetail,
  processOrder,
  approveOrder,
} from '../utils/o2cApi';

const EMPTY_DRAFT = {
  customer_id: '',
  po_number: '',
  currency: 'USD',
  lines: [],
};

const PAGE_META = {
  upload: {
    title: 'Upload Purchase Order',
    subtitle: 'Drop a customer PO document and scan for extraction.',
  },
  tracker: {
    title: 'Review Purchase Order',
    subtitle: 'Validate extracted values against source and confirm.',
  },
  dashboard: {
    title: 'Process Dashboard',
    subtitle: 'Track all submitted purchase orders and approvals.',
  },
};

function asNum(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatMoney(value, currency = 'USD') {
  const amount = Number(value) || 0;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (_err) {
    return `${currency || 'USD'} ${amount.toFixed(2)}`;
  }
}

function formatDate(value) {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return value;
  return dt.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getStatusTone(status) {
  const s = String(status || '').toLowerCase();
  if (/invoice/.test(s) || s === 'invoiced') return 'success';
  if (/reject/.test(s)) return 'danger';
  if (/pause|pending/.test(s)) return 'warn';
  if (/process|created/.test(s)) return 'info';
  return 'muted';
}

function statusLabel(status) {
  const s = String(status || '').toLowerCase();
  if (!s) return 'unknown';
  return s.replace(/_/g, ' ');
}

/* ─── SVG Icons ────────────────────────────────────────────── */
const IconUpload = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 16 12 12 8 16" />
    <line x1="12" y1="12" x2="12" y2="21" />
    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
  </svg>
);

const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconRefresh = ({ spinning }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ animation: spinning ? 'o2cSpin 0.8s linear infinite' : 'none' }}>
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

const IconArrowLeft = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

const IconSearch = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const IconFile = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const IconTrash = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const IconFilter = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

const IconAlert = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const IconBox = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);

/* ─── Step Indicator ───────────────────────────────────────── */
const STEPS = ['Upload PO', 'Review & Edit', 'Dashboard'];
const StepIndicator = ({ current }) => {
  const idx = current === 'upload' ? 0 : current === 'tracker' ? 1 : 2;
  return (
    <div className="o2c-steps">
      {STEPS.map((label, i) => (
        <React.Fragment key={label}>
          <div className={`o2c-step ${i < idx ? 'past' : i === idx ? 'current' : ''}`}>
            <div className="o2c-step-circle">
              {i < idx ? <IconCheck /> : <span>{i + 1}</span>}
            </div>
            <span className="o2c-step-label">{label}</span>
          </div>
          {i < STEPS.length - 1 && (
            <div className={`o2c-step-line ${i < idx ? 'done' : ''}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

/* ─── Count-up number animation ───────────────────────────── */
const CountUp = ({ target, duration = 1100 }) => {
  const [count, setCount] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    setCount(0);
    if (!target) return;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 4); // easeOutQuart
      setCount(Math.round(eased * target));
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration]);
  return <>{count}</>;
};

/* ─── Ripple on button click ───────────────────────────────── */
const useRipple = () =>
  useCallback((e) => {
    const btn = e.currentTarget;
    const rect = btn.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height) * 2.2;
    const span = document.createElement('span');
    span.className = 'o2c-ripple';
    Object.assign(span.style, {
      width:  `${size}px`,
      height: `${size}px`,
      left:   `${e.clientX - rect.left - size / 2}px`,
      top:    `${e.clientY - rect.top  - size / 2}px`,
    });
    btn.appendChild(span);
    span.addEventListener('animationend', () => span.remove());
  }, []);

/* ─── 3-D tilt on stat cards ───────────────────────────────── */
const useTilt = () => {
  const ref = useRef(null);
  const onMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const { left, top, width, height } = el.getBoundingClientRect();
    const x = (e.clientX - left) / width  - 0.5;
    const y = (e.clientY - top)  / height - 0.5;
    el.style.transform = `perspective(520px) rotateX(${(-y * 10).toFixed(2)}deg) rotateY(${(x * 10).toFixed(2)}deg) translateY(-4px) scale(1.02)`;
  }, []);
  const onLeave = useCallback(() => {
    if (ref.current) ref.current.style.transform = '';
  }, []);
  return { ref, onMouseMove: onMove, onMouseLeave: onLeave };
};

/* ─── Tilt-enabled stat card ───────────────────────────────── */
const StatCard = ({ label, value, sub, colorClass, delay }) => {
  const tilt = useTilt();
  return (
    <div
      className={`o2c-stat o2c-stat-enter`}
      style={{ '--delay': delay, transition: 'transform .18s ease, box-shadow .18s' }}
      {...tilt}
    >
      <div className={`o2c-stat-icon ${colorClass}`} />
      <div className="o2c-stat-text">
        <p>{label}</p>
        <strong><CountUp target={value} /></strong>
        <small>{sub}</small>
      </div>
    </div>
  );
};

/* ─── Skeleton row ─────────────────────────────────────────── */
const SkeletonRow = ({ cols = 10 }) => (
  <tr className="o2c-skeleton-row">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i}><div className="o2c-skeleton" /></td>
    ))}
  </tr>
);

/* ─── Order Detail Panel ───────────────────────────────────── */
const OrderDetailPanel = ({ order, detail, detailLoading, onClose, onApprove, onReject, onReprocess }) => {
  if (!order) return null;

  const isPending = ['pending_approval', 'paused'].includes(String(order.status || '').toLowerCase());

  // Normalise detail fields — try all known backend nesting paths
  const d = detail || {};
  const actionsLog  = d?.result?.actions_taken || d?.actions_taken || [];
  const approvalMsg = d?.approval_message      || d?.result?.approval_message || order?.approval_message || '';

  // Walk every plausible nesting path the API might use
  const lines = (
    d?.lines              ||
    d?.line_items         ||
    d?.items              ||
    d?.result?.lines      ||
    d?.result?.line_items ||
    d?.result?.items      ||
    d?.result?.order?.lines      ||
    d?.result?.order?.line_items ||
    d?.order?.lines       ||
    d?.order?.line_items  ||
    d?.data?.lines        ||
    d?.data?.line_items   ||
    order?.lines          ||
    order?.line_items     ||
    []
  ).filter(Boolean);

  // Normalise a single line item — handles snake_case and camelCase field names
  const normLine = (line, i) => ({
    no:         line.line_no      ?? line.lineNo      ?? line.line_number ?? line.LineNumber ?? (i + 1),
    material:   line.material_id  ?? line.materialId  ?? line.material    ?? line.Material   ?? '-',
    quantity:   line.quantity     ?? line.qty         ?? line.OrderQuantity ?? line.Quantity  ?? '-',
    uom:        line.uom          ?? line.UOM         ?? line.unit         ?? '-',
    unitPrice:  line.unit_price   ?? line.unitPrice   ?? line.UnitPrice   ?? null,
    listPrice:  line.list_price   ?? line.listPrice   ?? line.ListPrice   ?? null,
    lineTotal:  line.line_total   ?? line.lineTotal   ?? line.LineTotal   ?? line.net_value  ?? null,
  });

  return (
    <div className="o2c-detail-overlay" onClick={onClose}>
      <div className="o2c-detail-panel" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="o2c-dp-header">
          <div className="o2c-dp-title">
            <h2>{order.order_id}</h2>
            <div className="o2c-dp-meta">
              {order.customer_id && <span>{order.customer_id}</span>}
              {order.currency    && <><span className="o2c-dp-dot">·</span><span>{order.currency}</span></>}
              <span className={`o2c-status o2c-status-${getStatusTone(order.status)}`}>
                {statusLabel(order.status)}
              </span>
            </div>
          </div>
          <button className="o2c-dp-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {detailLoading && (
          <div className="o2c-dp-loading">
            <span className="o2c-spinner" style={{ width: 20, height: 20, borderWidth: 3 }} />
            <span>Loading detail…</span>
          </div>
        )}

        {!detailLoading && (
          <div className="o2c-dp-body">

            {/* Order info + SAP docs */}
            <div className="o2c-dp-grid2">
              <div className="o2c-dp-section">
                <div className="o2c-dp-section-title">📋 Order Info</div>
                <div className="o2c-dp-rows">
                  <div className="o2c-dp-row"><span>Order ID</span><strong>{order.order_id}</strong></div>
                  <div className="o2c-dp-row"><span>Customer</span><strong>{order.customer_id || '-'}</strong></div>
                  <div className="o2c-dp-row"><span>PO Number</span><strong>{order.po_number || '-'}</strong></div>
                  <div className="o2c-dp-row"><span>Currency</span><strong>{order.currency || '-'}</strong></div>
                  <div className="o2c-dp-row"><span>Total Value</span><strong>{formatMoney(order.total_value, order.currency)}</strong></div>
                  <div className="o2c-dp-row"><span>Created</span><strong>{formatDate(order.created_at)}</strong></div>
                  <div className="o2c-dp-row">
                    <span>Status</span>
                    <span className={`o2c-status o2c-status-${getStatusTone(order.status)}`}>
                      {statusLabel(order.status)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="o2c-dp-section">
                <div className="o2c-dp-section-title">🗂 SAP Documents</div>
                <div className="o2c-dp-rows">
                  <div className="o2c-dp-row"><span>Sales Order</span><strong className="o2c-dp-doc">{order.sap_so_number || '-'}</strong></div>
                  <div className="o2c-dp-row"><span>Delivery</span><strong className="o2c-dp-doc">{order.delivery_number || '-'}</strong></div>
                  <div className="o2c-dp-row"><span>Invoice</span><strong className="o2c-dp-doc">{order.invoice_number || '-'}</strong></div>
                </div>
              </div>
            </div>

            {/* Process log */}
            {actionsLog.length > 0 && (
              <div className="o2c-dp-section">
                <div className="o2c-dp-section-title">⚡ Process Log</div>
                <ul className="o2c-dp-log">
                  {actionsLog.map((item, i) => (
                    <li key={i}><span className="o2c-dp-log-check">✓</span>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Approval / pause message */}
            {approvalMsg && (
              <div className="o2c-dp-approval-msg">
                {approvalMsg}
              </div>
            )}

            {/* Line items */}
            {lines.length > 0 && (
              <div className="o2c-dp-section">
                <div className="o2c-dp-section-title">≡ Line Items</div>
                <div className="o2c-dp-table-wrap">
                  <table className="o2c-dp-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Material</th>
                        <th>QTY</th>
                        <th>UOM</th>
                        <th>Unit Price</th>
                        <th>List Price</th>
                        <th>Line Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line, i) => {
                        const n = normLine(line, i);
                        return (
                          <tr key={i}>
                            <td>{n.no}</td>
                            <td>{n.material}</td>
                            <td>{n.quantity}</td>
                            <td>{n.uom}</td>
                            <td>{n.unitPrice != null ? formatMoney(n.unitPrice, order.currency) : '-'}</td>
                            <td>{n.listPrice != null ? formatMoney(n.listPrice, order.currency) : '-'}</td>
                            <td>{n.lineTotal != null ? formatMoney(n.lineTotal, order.currency) : '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Action buttons */}
            {isPending ? (
              <div className="o2c-dp-actions">
                <button className="o2c-btn success" onClick={onApprove} disabled={detailLoading}>Approve</button>
                <button className="o2c-btn danger"  onClick={onReject}  disabled={detailLoading}>Reject</button>
              </div>
            ) : (
              <div className="o2c-dp-actions">
                <button className="o2c-btn" onClick={onReprocess} disabled={detailLoading}>
                  {detailLoading ? <><span className="o2c-spinner" /> Processing…</> : 'Re-Process'}
                </button>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Main component ───────────────────────────────────────── */
const O2CAgent = () => {
  const ripple = useRipple();
  const navigate = useNavigate();
  const location = useLocation();
  const [activePage, setActivePage] = useState('upload');
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const [ingestLoading, setIngestLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [ingestResult, setIngestResult] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [confirmResult, setConfirmResult] = useState(null);

  const [orders, setOrders] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [detail, setDetail] = useState(null);
  const [toastMessage, setToastMessage] = useState('');
  const [showDetail, setShowDetail] = useState(false);

  /* search + filter */
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const selectedOrder = useMemo(
    () => orders.find((o) => o.order_id === selectedOrderId) || null,
    [orders, selectedOrderId]
  );

  const draftTotal = useMemo(
    () => (draft.lines || []).reduce((sum, line) => sum + (asNum(line.quantity) * asNum(line.unit_price)), 0),
    [draft.lines]
  );

  const pageMeta = PAGE_META[activePage] || PAGE_META.upload;

  const dashboardStats = useMemo(() => {
    const byStatus = (orders || []).reduce((acc, order) => {
      const key = String(order?.status || 'unknown').toLowerCase();
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const totalValue = (orders || []).reduce((sum, order) => sum + (Number(order?.total_value) || 0), 0);
    return {
      totalOrders: orders.length,
      processing: (byStatus.processing || 0) + (byStatus.created || 0),
      invoiced: byStatus.invoiced || 0,
      pendingApproval: byStatus.pending_approval || byStatus.paused || 0,
      rejected: byStatus.rejected || 0,
      totalValue,
      byStatus,
    };
  }, [orders]);

  const approvalQueue = useMemo(
    () => orders.filter((o) => {
      const s = String(o?.status || '').toLowerCase();
      return s === 'pending_approval' || s === 'paused';
    }),
    [orders]
  );

  const activeApprovalOrder = useMemo(() => {
    if (selectedOrderId) {
      const selected = approvalQueue.find((o) => o.order_id === selectedOrderId);
      if (selected) return selected;
    }
    return approvalQueue[0] || null;
  }, [approvalQueue, selectedOrderId]);

  const filteredOrders = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return orders.filter((o) => {
      const matchSearch = !q ||
        (o.order_id || '').toLowerCase().includes(q) ||
        (o.customer_id || '').toLowerCase().includes(q) ||
        (o.po_number || '').toLowerCase().includes(q) ||
        (o.sap_so_number || '').toLowerCase().includes(q);
      const matchStatus = statusFilter === 'all' ||
        String(o.status || '').toLowerCase().includes(statusFilter);
      return matchSearch && matchStatus;
    });
  }, [orders, searchQuery, statusFilter]);

  const updateHeader = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const updateLine = (index, field, value) => {
    setDraft((prev) => ({
      ...prev,
      lines: prev.lines.map((line, idx) => {
        if (idx !== index) return line;
        if (['line_no', 'quantity', 'unit_price'].includes(field)) {
          return { ...line, [field]: asNum(value) };
        }
        return { ...line, [field]: value };
      }),
    }));
  };

  const addLine = () => {
    setDraft((prev) => ({
      ...prev,
      lines: [
        ...prev.lines,
        { line_no: prev.lines.length + 1, material_id: '', quantity: 1, unit_price: 0, uom: 'EA' },
      ],
    }));
  };

  const removeLine = (index) => {
    setDraft((prev) => ({
      ...prev,
      lines: prev.lines
        .filter((_, idx) => idx !== index)
        .map((line, idx) => ({ ...line, line_no: idx + 1 })),
    }));
  };

  const clearMessages = () => { setError(''); setSuccess(''); };

  const loadOrderDetail = async (orderId) => {
    if (!orderId) return;
    setDetailLoading(true);
    setError('');
    try {
      const data = await getOrderDetail(orderId);
      setDetail(data);
    } catch (err) {
      setError(err.message || 'Failed to load order detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshOrders = async (preferredId) => {
    setOrdersLoading(true);
    setError('');
    try {
      const data = await getOrders();
      const list = Array.isArray(data?.orders) ? data.orders : [];
      setOrders(list);
      const nextId = preferredId || selectedOrderId || list[0]?.order_id || '';
      setSelectedOrderId(nextId);
      if (nextId) {
        await loadOrderDetail(nextId);
      } else {
        setDetail(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to load orders');
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleIngest = async () => {
    if (!file) { setError('Select a file first.'); return; }
    setIngestLoading(true);
    clearMessages();
    setConfirmResult(null);
    try {
      const data = await ingestPurchaseOrder(file);
      setIngestResult(data);
      setDraft(data?.draft_order || data?.extracted || EMPTY_DRAFT);
      setSuccess(data?.message || 'PO extracted successfully. Review and confirm.');
      setToastMessage('PO extracted. Review fields and confirm when ready.');
      navigate('/o2c-agent/tracker');
    } catch (err) {
      setError(err.message || 'Failed to ingest PO');
    } finally {
      setIngestLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!draft.customer_id || !draft.po_number || !draft.currency || draft.lines.length === 0) {
      setError('Customer ID, PO number, currency, and at least one line are required.');
      return;
    }
    const normalizedLines = (draft.lines || [])
      .map((line, idx) => ({
        line_no: asNum(line.line_no) || idx + 1,
        material_id: String(line.material_id || '').trim(),
        quantity: asNum(line.quantity),
        unit_price: asNum(line.unit_price),
        uom: String(line.uom || '').trim() || 'EA',
      }))
      .filter((line) => line.material_id);
    if (normalizedLines.length === 0) {
      setError('At least one line with a material ID is required.');
      return;
    }
    const invalidLine = normalizedLines.find((l) => l.quantity <= 0 || l.unit_price < 0);
    if (invalidLine) {
      setError(`Invalid values in line ${invalidLine.line_no}.`);
      return;
    }
    const payload = {
      customer_id: String(draft.customer_id).trim(),
      po_number: String(draft.po_number).trim(),
      currency: String(draft.currency).trim().toUpperCase(),
      lines: normalizedLines,
    };
    setConfirmLoading(true);
    clearMessages();
    try {
      const data = await confirmIngestedPurchaseOrder(payload);
      setConfirmResult(data);
      setSuccess(data?.message ?? 'PO processed successfully');
      await refreshOrders(data?.order_id);
      setToastMessage(`${data?.order_id || 'Order'} submitted to O2C pipeline.`);
      navigate('/o2c-agent/dashboard');
    } catch (err) {
      setError(err.message || 'Failed to confirm PO');
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleReprocess = async () => {
    if (!selectedOrderId) return;
    setDetailLoading(true);
    clearMessages();
    try {
      const data = await processOrder(selectedOrderId);
      setSuccess(data?.message ?? 'Order processed successfully');
      await loadOrderDetail(selectedOrderId);
      await refreshOrders(selectedOrderId);
      setToastMessage(`${selectedOrderId} re-processed successfully.`);
    } catch (err) {
      setError(err.message || 'Failed to process order');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDecision = async (decision) => {
    if (!selectedOrderId) return;
    setDetailLoading(true);
    clearMessages();
    try {
      const data = await approveOrder(selectedOrderId, decision);
      setSuccess(data?.message ?? `Order ${decision}d successfully`);
      await loadOrderDetail(selectedOrderId);
      await refreshOrders(selectedOrderId);
      setToastMessage(`${selectedOrderId} ${decision === 'approve' ? 'approved ✓' : 'rejected'}.`);
    } catch (err) {
      setError(err.message || `Failed to ${decision} order`);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (location.pathname.endsWith('/dashboard')) { setActivePage('dashboard'); return; }
    if (location.pathname.endsWith('/tracker'))   { setActivePage('tracker');   return; }
    setActivePage('upload');
  }, [location.pathname]);

  useEffect(() => {
    if ((activePage === 'dashboard' || activePage === 'tracker') && orders.length === 0) {
      refreshOrders();
    }
  }, [activePage]);

  useEffect(() => {
    if (!toastMessage) return undefined;
    const timer = setTimeout(() => setToastMessage(''), 3400);
    return () => clearTimeout(timer);
  }, [toastMessage]);

  useEffect(() => {
    if (activeApprovalOrder?.order_id) setSelectedOrderId(activeApprovalOrder.order_id);
  }, [activeApprovalOrder?.order_id]);

  const handleRowClick = async (orderId) => {
    setSelectedOrderId(orderId);
    setShowDetail(true);
    await loadOrderDetail(orderId);
  };

  const navigateToPage = (page) => {
    navigate(page === 'dashboard' ? '/o2c-agent/dashboard' : page === 'tracker' ? '/o2c-agent/tracker' : '/o2c-agent/upload');
  };

  const triggerFilePicker = () => fileInputRef.current?.click();

  const onFileSelected = (selected) => {
    if (!selected) return;
    setFile(selected);
    clearMessages();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    const dropped = event.dataTransfer?.files?.[0];
    if (dropped) onFileSelected(dropped);
  };

  /* file extension helper */
  const fileExt = file ? file.name.split('.').pop().toUpperCase() : null;

  return (
    <div className="o2c-page">

      {/* ── Topbar ── */}
      <header className="o2c-topbar">
        <div className="o2c-title">
          <h1>{pageMeta.title}</h1>
          <p>{pageMeta.subtitle}</p>
        </div>
        <div className="o2c-topbar-actions">
          <button type="button" className="o2c-btn" onClick={() => refreshOrders()} disabled={ordersLoading}>
            <IconRefresh spinning={ordersLoading} />
            {ordersLoading ? 'Refreshing…' : 'Refresh'}
          </button>
          <button type="button" className="o2c-btn" onClick={() => navigate('/home')}>
            <IconArrowLeft /> Back to Home
          </button>
        </div>
      </header>

      {/* ── Step indicator ── */}
      <StepIndicator current={activePage} />

      {/* ── Tab nav ── */}
      <nav className="o2c-page-nav" aria-label="O2C pages">
        {['upload', 'tracker', 'dashboard'].map((page) => (
          <button
            key={page}
            type="button"
            className={`o2c-page-btn ${activePage === page ? 'active' : ''}`}
            onClick={() => navigateToPage(page)}
          >
            {page === 'upload' && '📄 '}
            {page === 'tracker' && '✏️ '}
            {page === 'dashboard' && '📊 '}
            {page === 'upload' ? 'Upload Purchase Order' : page === 'tracker' ? 'Review Purchase Order' : 'Process Dashboard'}
            {page === 'dashboard' && orders.length > 0 && (
              <span className="o2c-tab-badge">{orders.length}</span>
            )}
          </button>
        ))}
      </nav>

      {/* ── Messages ── */}
      {error   && <div className="o2c-error"   role="alert"><IconAlert />{error}</div>}
      {success && <div className="o2c-success"  role="status"><IconCheck />{success}</div>}

      {/* ════════════════════════════════ UPLOAD PAGE ════════════════════════════════ */}
      {activePage === 'upload' && (
        <div className="o2c-card o2c-animate-in">
          <div className="o2c-card-head">
            <h2>Upload Purchase Order</h2>
            <p>Drop a customer PO document, review extracted fields side by side with source, then confirm before the O2C pipeline starts.</p>
          </div>

          <div className="o2c-upload-layout">
            {/* Drop zone panel */}
            <div className="o2c-upload-card">
              <h3>Drop Zone</h3>
              <div
                className={`o2c-drop-zone ${dragActive ? 'active' : ''} ${file ? 'has-file' : ''}`}
                onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
                onDragOver={(e)  => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
                onDrop={handleDrop}
                onClick={triggerFilePicker}
                role="button"
                tabIndex={0}
              >
                <div className={`o2c-drop-icon ${dragActive ? 'bounce' : ''}`}>
                  <IconUpload />
                </div>
                <h4>{dragActive ? 'Release to upload' : 'Drag & drop your PO here'}</h4>
                <p>or click anywhere to browse</p>
                <button type="button" className="o2c-btn primary" onClick={(e) => { e.stopPropagation(); triggerFilePicker(); }}>
                  Browse Files
                </button>
                <div className="o2c-file-tags">
                  {['PDF', 'PNG', 'JPG', 'XLSX', 'CSV'].map((t) => (
                    <span key={t}>{t}</span>
                  ))}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,image/*,.xlsx,.xls,.csv"
                  onChange={(e) => onFileSelected(e.target.files?.[0] || null)}
                  className="o2c-hidden-input"
                />
              </div>

              {file && (
                <div className="o2c-picked-file o2c-animate-in">
                  <div className="o2c-picked-file-info">
                    <div className="o2c-file-ext-badge">{fileExt}</div>
                    <span>{file.name}</span>
                  </div>
                  <small>{Math.max(1, Math.round(file.size / 1024))} KB</small>
                </div>
              )}

              <button
                type="button"
                className="o2c-btn dark-block"
                onClick={(e) => { ripple(e); handleIngest(); }}
                disabled={!file || ingestLoading}
              >
                {ingestLoading ? (
                  <><span className="o2c-spinner" />Uploading and scanning…</>
                ) : (
                  <><IconUpload /> Scan PO For Review</>
                )}
              </button>

              {(ingestLoading || ingestResult) && (
                <ol className="o2c-scan-steps">
                  <li className="done">Uploading and scanning document</li>
                  <li className={ingestResult ? 'done' : 'active'}>Preparing side-by-side review</li>
                  <li className={ingestResult ? 'done' : ''}>Waiting for user confirmation</li>
                </ol>
              )}
            </div>

            {/* How it works panel */}
            <div className="o2c-how-card">
              <h3>How It Works</h3>
              <ol>
                <li>
                  <strong>Upload PO Document</strong>
                  <p>Drop a PDF, image, or spreadsheet of any customer purchase order.</p>
                </li>
                <li>
                  <strong>AI Extraction</strong>
                  <p>The model extracts customer, PO number, line items, quantities, and prices.</p>
                </li>
                <li>
                  <strong>Review and Correct</strong>
                  <p>Validate extracted values, fix any OCR errors, and confirm final inputs.</p>
                </li>
                <li>
                  <strong>SAP Sales Order to Invoice</strong>
                  <p>After confirmation, O2C processes end-to-end and handles approvals if needed.</p>
                </li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════ TRACKER PAGE ═══════════════════════════════ */}
      {activePage === 'tracker' && (
        <div className="o2c-card o2c-animate-in">
          <div className="o2c-card-head">
            <h2>Review Purchase Order</h2>
            <p>Validate extracted values against the original PO, correct mistakes, and confirm when data looks right.</p>
          </div>

          <div className="o2c-review-file-row">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <IconFile />
              <span>{file?.name || ingestResult?.filename || 'No file uploaded yet'}</span>
            </div>
            <span className="o2c-line-count-badge">{draft.lines.length} line{draft.lines.length !== 1 ? 's' : ''}</span>
          </div>

          <div className="o2c-review-layout">
            {/* Left: original */}
            <section className="o2c-review-left">
              <header>
                <h3>Original Purchase Order</h3>
                <small>{file?.name || ingestResult?.filename || '-'}</small>
              </header>
              <div className="o2c-preview-placeholder">
                <div className="o2c-preview-icon"><IconBox /></div>
                <strong>{file?.name || ingestResult?.filename || 'No document selected'}</strong>
                <p>Live preview is available for PDF and image files.<br />You can still review and edit extracted fields before processing.</p>
              </div>
            </section>

            {/* Right: editable fields */}
            <section className="o2c-review-right">
              <header>
                <h3>Editable Extracted Fields</h3>
                <small>Used for sales order creation</small>
              </header>

              <div className="o2c-review-right-body">
              <p className="o2c-help-banner">
                Compare uploaded PO with extracted values and update as needed.
              </p>

              <div className="o2c-grid o2c-grid-header">
                <label className="o2c-label">
                  Customer ID
                  <input type="text" value={draft.customer_id || ''} onChange={(e) => updateHeader('customer_id', e.target.value)} placeholder="e.g. CUST-001" />
                </label>
                <label className="o2c-label">
                  PO Number
                  <input type="text" value={draft.po_number || ''} onChange={(e) => updateHeader('po_number', e.target.value)} placeholder="e.g. PO-2024-001" />
                </label>
                <label className="o2c-label" style={{ gridColumn: '1 / -1' }}>
                  Currency
                  <input type="text" maxLength={3} value={draft.currency || ''} onChange={(e) => updateHeader('currency', e.target.value.toUpperCase())} placeholder="USD" style={{ maxWidth: 120 }} />
                </label>
              </div>

              <div className="o2c-review-lines-title">
                <h4>Line Items ({draft.lines.length})</h4>
                <button type="button" className="o2c-btn" onClick={addLine}>
                  <IconPlus /> Add Line
                </button>
              </div>

              {(draft.lines || []).map((line, index) => (
                <div key={`${line.line_no}-${index}`} className="o2c-line-card o2c-line-enter">
                  <div className="o2c-line-card-head">
                    <span>Line {index + 1}</span>
                    <button type="button" className="o2c-btn danger icon-btn" onClick={() => removeLine(index)}>
                      <IconTrash />
                    </button>
                  </div>
                  <div className="o2c-line-grid">
                    <label className="o2c-label">
                      Line No
                      <input type="number" min="1" value={line.line_no} onChange={(e) => updateLine(index, 'line_no', e.target.value)} />
                    </label>
                    <label className="o2c-label">
                      Material ID
                      <input type="text" value={line.material_id || ''} onChange={(e) => updateLine(index, 'material_id', e.target.value)} placeholder="MAT-001" />
                    </label>
                    <label className="o2c-label">
                      Quantity
                      <input type="number" min="1" value={line.quantity} onChange={(e) => updateLine(index, 'quantity', e.target.value)} />
                    </label>
                    <label className="o2c-label">
                      Unit Price
                      <input type="number" min="0" step="0.01" value={line.unit_price} onChange={(e) => updateLine(index, 'unit_price', e.target.value)} />
                    </label>
                    <label className="o2c-label">
                      UOM
                      <input type="text" value={line.uom || ''} onChange={(e) => updateLine(index, 'uom', e.target.value)} placeholder="EA" />
                    </label>
                  </div>
                </div>
              ))}

              {draft.lines.length === 0 && (
                <p className="o2c-hint">No lines extracted yet. Click "Add Line" to add manually.</p>
              )}

              </div>{/* end o2c-review-right-body */}

              <div className="o2c-review-footer">
                <div className="o2c-total-display">
                  <span>Total</span>
                  <strong>{formatMoney(draftTotal, draft.currency)}</strong>
                </div>
                <div className="o2c-action-row">
                  <button type="button" className="o2c-btn" onClick={() => navigate('/o2c-agent/upload')}>
                    <IconArrowLeft /> Back
                  </button>
                  <button type="button" className="o2c-btn dark-block" onClick={(e) => { ripple(e); handleConfirm(); }} disabled={confirmLoading}>
                    {confirmLoading ? (
                      <><span className="o2c-spinner" />Confirming…</>
                    ) : (
                      <>Confirm & Start O2C</>
                    )}
                  </button>
                </div>
              </div>
            </section>
          </div>
        </div>
      )}

      {/* ════════════════════════════════ DASHBOARD PAGE ═════════════════════════════ */}
      {activePage === 'dashboard' && (
        <div className="o2c-card o2c-animate-in">
          <div className="o2c-card-head">
            <h2>Process Dashboard</h2>
            <p>Real-time O2C pipeline status for all submitted purchase orders.</p>
          </div>

          {/* Stats */}
          <div className="o2c-stats">
            <StatCard label="Total Orders"     value={dashboardStats.totalOrders}    sub="all time"    colorClass="c1" delay="0ms"   />
            <StatCard label="Processing"        value={dashboardStats.processing}     sub="in pipeline" colorClass="c2" delay="60ms"  />
            <StatCard label="Awaiting Approval" value={dashboardStats.pendingApproval} sub="need action" colorClass="c3" delay="120ms" />
            <StatCard label="Completed"         value={dashboardStats.invoiced}       sub="invoiced"    colorClass="c4" delay="180ms" />
            <StatCard label="Errors / Holds"    value={dashboardStats.rejected}       sub="need review" colorClass="c5" delay="240ms" />
          </div>

          {/* Approval queue */}
          {activeApprovalOrder && (
            <section className="o2c-approval-queue o2c-animate-in">
              <div className="o2c-approval-head">
                <h3><IconAlert /> Human Approval Required</h3>
                <span>{approvalQueue.length} waiting</span>
              </div>
              <div className="o2c-approval-item">
                <div>
                  <div className="o2c-approval-meta">
                    <strong>{activeApprovalOrder.order_id}</strong>
                    <span className={`o2c-status o2c-status-${getStatusTone(activeApprovalOrder.status)}`}>
                      {statusLabel(activeApprovalOrder.status)}
                    </span>
                  </div>
                  <p>
                    Customer <b>{activeApprovalOrder.customer_id || '-'}</b>
                    {' · '}PO <b>{activeApprovalOrder.po_number || '-'}</b>
                    {' · '}{(activeApprovalOrder.currency || 'USD')} {Number(activeApprovalOrder.total_value || 0).toLocaleString()}
                    {activeApprovalOrder.sap_so_number ? ` · SAP SO ${activeApprovalOrder.sap_so_number}` : ''}
                  </p>
                  <div className="o2c-approval-message">
                    {activeApprovalOrder.approval_message || 'Order paused by the agent and waiting for decision.'}
                  </div>
                </div>
                <div className="o2c-approval-actions">
                  <button
                    type="button"
                    className="o2c-btn success"
                    onClick={async () => { setSelectedOrderId(activeApprovalOrder.order_id); await handleDecision('approve'); }}
                    disabled={detailLoading}
                  >
                    {detailLoading ? <span className="o2c-spinner" /> : null} Approve
                  </button>
                  <button
                    type="button"
                    className="o2c-btn danger"
                    onClick={async () => { setSelectedOrderId(activeApprovalOrder.order_id); await handleDecision('reject'); }}
                    disabled={detailLoading}
                  >
                    Reject
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* Orders table */}
          <section className="o2c-orders-block">
            <div className="o2c-orders-head">
              <h3>Orders</h3>
              <div className="o2c-table-controls">
                <div className="o2c-search-wrap">
                  <IconSearch />
                  <input
                    type="text"
                    className="o2c-search-input"
                    placeholder="Search orders…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="o2c-filter-wrap">
                  <IconFilter />
                  <select
                    className="o2c-filter-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="created">Created</option>
                    <option value="processing">Processing</option>
                    <option value="pending">Pending Approval</option>
                    <option value="invoiced">Invoiced</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <span className="o2c-orders-count">{filteredOrders.length} order{filteredOrders.length !== 1 ? 's' : ''}</span>
              </div>
            </div>

            <div className="o2c-table-wrap">
              <table className="o2c-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Customer</th>
                    <th>PO Number</th>
                    <th>Status</th>
                    <th>SAP SO</th>
                    <th>Delivery</th>
                    <th>Invoice</th>
                    <th>Total Value</th>
                    <th>Created At</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {ordersLoading && (
                    <>
                      <SkeletonRow cols={10} />
                      <SkeletonRow cols={10} />
                      <SkeletonRow cols={10} />
                      <SkeletonRow cols={10} />
                    </>
                  )}
                  {!ordersLoading && filteredOrders.length === 0 && (
                    <tr>
                      <td colSpan={10}>
                        <div className="o2c-empty-state">
                          <div className="o2c-empty-icon"><IconBox /></div>
                          <strong>{orders.length === 0 ? 'No orders yet' : 'No results found'}</strong>
                          <p>{orders.length === 0 ? 'Upload and confirm a PO to populate the dashboard.' : 'Try adjusting your search or filter.'}</p>
                        </div>
                      </td>
                    </tr>
                  )}
                  {!ordersLoading && filteredOrders.map((order, idx) => (
                    <tr
                      key={order.order_id}
                      className="o2c-table-row-enter o2c-table-row-clickable"
                      style={{ '--row-delay': `${idx * 30}ms` }}
                      onClick={() => handleRowClick(order.order_id)}
                    >
                      <td className="o2c-order-link">{order.order_id}</td>
                      <td>{order.customer_id || '-'}</td>
                      <td>{order.po_number || '-'}</td>
                      <td>
                        <span className={`o2c-status o2c-status-${getStatusTone(order.status)}`}>
                          {statusLabel(order.status)}
                        </span>
                      </td>
                      <td>{order.sap_so_number || '-'}</td>
                      <td>{order.delivery_number || '-'}</td>
                      <td>{order.invoice_number || '-'}</td>
                      <td>{formatMoney(order.total_value || 0, order.currency || draft.currency)}</td>
                      <td>{formatDate(order.created_at)}</td>
                      <td>
                        {(String(order.status || '').toLowerCase() === 'pending_approval' ||
                          String(order.status || '').toLowerCase() === 'paused') ? (
                          <div className="o2c-action-row" onClick={(e) => e.stopPropagation()}>
                            <button type="button" className="o2c-btn success"
                              onClick={async (e) => { e.stopPropagation(); setSelectedOrderId(order.order_id); await handleDecision('approve'); }}
                              disabled={detailLoading}>Approve</button>
                            <button type="button" className="o2c-btn danger"
                              onClick={async (e) => { e.stopPropagation(); setSelectedOrderId(order.order_id); await handleDecision('reject'); }}
                              disabled={detailLoading}>Reject</button>
                          </div>
                        ) : (
                          <button type="button" className="o2c-btn"
                            onClick={async (e) => { e.stopPropagation(); setSelectedOrderId(order.order_id); await loadOrderDetail(order.order_id); await handleReprocess(); }}
                            disabled={detailLoading}>Re-Process</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Action log */}
          {detail?.result?.actions_taken?.length > 0 && (
            <div className="o2c-action-log o2c-animate-in">
              <h4>Latest Workflow Actions</h4>
              <ul>
                {detail.result.actions_taken.map((item, idx) => (
                  <li key={`${item}-${idx}`}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ── Toast ── */}
      {/* ── Order Detail Panel ── */}
      {showDetail && (
        <OrderDetailPanel
          order={selectedOrder || orders.find(o => o.order_id === selectedOrderId)}
          detail={detail}
          detailLoading={detailLoading}
          onClose={() => setShowDetail(false)}
          onApprove={async () => { await handleDecision('approve'); }}
          onReject={async ()  => { await handleDecision('reject');  }}
          onReprocess={async () => { await handleReprocess(); }}
        />
      )}

      {toastMessage && (
        <div className="o2c-toast">
          <IconCheck />
          {toastMessage}
        </div>
      )}
    </div>
  );
};

export default O2CAgent;
